import React, { useCallback } from 'react'

import { format } from 'date-fns'
import Link from 'next/link'
import {
  HiOutlineBookOpen,
  HiOutlineMagnifyingGlass,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2'

import Avatar from '@/components/common/Avatar'
import Button from '@/components/common/Button'
import { CommercialOnly } from '@/components/common/CommercialOnly'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import {
  useHomeDashboardOpenCreatePursuitDialog,
  useHomeDashboardOpenUploadLibraryFileDialog,
} from '@/components/home/dashboard/context'
import { getNewOpportunitiesUrl } from '@/components/home/dashboard/lib'
import { PremiumAccessPopover } from '@/components/home/ui/PremiumAccessPopover'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

export const Header: React.FC = () => {
  const { user } = useAuthInfoWithThrow()
  const { activeOrg, isLoading: activeOrgIsLoading } = useActiveOrg()
  const { canAccessPremium } = useCanAccessPremium()
  const openCreatePursuitDialog = useHomeDashboardOpenCreatePursuitDialog()
  const openUploadLibraryFileDialog =
    useHomeDashboardOpenUploadLibraryFileDialog()

  const handleOpenLibraryFileDialog = useCallback(() => {
    if (!canAccessPremium) {
      return
    }
    openUploadLibraryFileDialog()
  }, [canAccessPremium, openUploadLibraryFileDialog])

  const greeting =
    'Good' +
    (new Date().getHours() < 12
      ? ' morning'
      : new Date().getHours() < 17
        ? ' afternoon'
        : ' evening')

  const newOpportunitiesUrl = activeOrg
    ? getNewOpportunitiesUrl(activeOrg)
    : '#'

  return (
    <>
      <div className="flex flex-col lg:flex-row items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link href="/settings" className="hidden md:block">
            <div className="rounded-full border border-primary-200 dark:border-primary-700">
              <Avatar
                name={`${user.firstName} ${user.lastName}`}
                src={user.pictureUrl}
                width={40}
                height={40}
                noTooltip={true}
                triggerClassName="cursor-pointer"
              />
            </div>
          </Link>
          <div className="flex flex-col gap-y-1 md:gap-y-0">
            <h1 className="text-xl font-bold text-primary-800 dark:text-primary-100">
              {greeting + ', ' + user.firstName}
            </h1>
            <div className="flex flex-wrap flex-shrink-1 items-start md:items-center gap-[6px] text-sm text-primary-500 dark:text-primary-400">
              {activeOrg && !activeOrgIsLoading ? (
                <span>{activeOrg?.name}</span>
              ) : (
                <Skeleton className="w-24 h-3 rounded-full" />
              )}
              <span>{'\u2022'}</span>
              <span>{format(new Date(), 'EEEE, MMMM do')}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <CommercialOnly>
            <OrgPermissionGate permission="read" fallback="hide">
              <Button variant="green-compact" className="shadow-sm">
                <Link href={newOpportunitiesUrl}>
                  <div className="flex items-center gap-x-[6px]">
                    <HiOutlineMagnifyingGlass size={16} />
                    <span>Find New Opportunities</span>
                  </div>
                </Link>
              </Button>
            </OrgPermissionGate>
          </CommercialOnly>
          <OrgPermissionGate permission="write" fallback="hide">
            <Button
              variant="green-compact"
              className="shadow-sm hidden md:flex"
              onClick={openCreatePursuitDialog}
            >
              <HiOutlineSquare3Stack3D size={16} />
              <span>New Pursuit</span>
            </Button>
          </OrgPermissionGate>
          <OrgPermissionGate permission="write" fallback="hide">
            <PremiumAccessPopover>
              <Button
                variant="blue-compact"
                className="shadow-sm hidden md:flex"
                onClick={handleOpenLibraryFileDialog}
              >
                <HiOutlineBookOpen size={16} />
                <span>Upload to Library</span>
              </Button>
            </PremiumAccessPopover>
          </OrgPermissionGate>
        </div>
      </div>
    </>
  )
}

Header.displayName = 'Home.Header'
